/*
 *  Copyright 2019 Adobe Systems Incorporated. All rights reserved.
 *  This file is licensed to you under the Apache License, Version 2.0 (the "License");
 *  you may not use this file except in compliance with the License. You may obtain a copy
 *  of the License at http://www.apache.org/licenses/LICENSE-2.0
 *
 *  Unless required by applicable law or agreed to in writing, software distributed under
 *  the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR REPRESENTATIONS
 *  OF ANY KIND, either express or implied. See the License for the specific language
 *  governing permissions and limitations under the License.
 *
 */
import ReactDOM from 'react-dom';
import React, { Component } from 'react';
import { ConnectedProps, connect } from 'react-redux';
import { WrappedComponentProps, injectIntl } from 'react-intl';
import debounce from 'lodash.debounce';
import type { HostContext, Logger, RootState, imsData } from '../../@types';
import {
	fetchAppMetadataList,
	fetchFilesForAutocomplete,
	fetchIntentShortcuts,
	fetchOnboardingSummaryItems,
	fetchPresearchData,
	fetchPresearchRecentFiles,
	fetchSearchAssetsResults,
	fetchSearchAutoCompleteItems,
	fetchSearchHistoryItems,
	fetchUserPreferences,
	getLeoSession,
	resetSearch,
	setExactTypedSearchQuery,
	setFilteredSearchDropdownItems,
	setSearchDropdownCurrentSectionItem,
	setSearchHistoryItems,
	setSearchQuery,
	setSubmittedSearchQuery,
	setTempSearchQuery,
	setTheme,
	showSearchDropdown,
	updateLeftnavStatus,
	updatePreSearchRecommendations,
} from '../../actions/searchActions';
import SearchUtils from '../../utils/SearchUtils';
import Search from '@react/react-spectrum/Search';
import SearchDropdown from '../SearchDropdown/SearchDropdown';
import SearchService from '../../services/SearchService';
import FileTypeService from '../../services/FileTypeService';
import Close from '@react/react-spectrum/Icon/Close';
import SearchAnalytics from '../../analytics/SearchAnalytics';
import './SearchBar.scss';
import SearchHostContext from '../../utils/SearchHostContext';
import { appendOnboardingSection, removeOnboardingSection } from '../../components/SearchBarOnboardingSection';

type OwnProps = {
	searchHostContext: HostContext;
	logger?: Logger;
	imsData?: imsData;
	featureFlags?: Array<string>;
	placeholder?: string;
	maxLength?: string;
	onChange?: (...args: Array<any>) => any;
	onSubmit?: (...args: Array<any>) => any;
	initialValue?: string;
	value?: string;
	tempSearchQuery?: string;
	setCurrentSearchView?: (...args: Array<any>) => any;
	searchHistoryItems?: Array<string>;
	searchTrendingItems?: Array<string>;
	hostShowSearchDropdown?: boolean;
	tempSearchQueryValue?: string;
	theme?: string;
	leftNavOn?: boolean;
	searchBarRootRef?: React.MutableRefObject<HTMLElement | null>;
};
type State = {
	showSearchBarFocus: boolean;
	quickLinkSelected: boolean;
	placeholderList: Array<string>;
	placeholderSelectedIndex: number;
	placeholderSelectedValue: string;
	showGenAiText: boolean;
};
type SearchBarProps = OwnProps & PropsFromRedux & WrappedComponentProps;
/*
 * SearchBar for Search
 */

class SearchBar extends Component<SearchBarProps, State> {
	searchBar: any;
	searchBarDropdown: any;
	searchBarContainerRef: any = React.createRef();
	private hasFetchedAppMetadataStatus = false;
	constructor(props: SearchBarProps) {
		super(props);
		this.state = {
			showSearchBarFocus: false,
			quickLinkSelected: false,
			placeholderSelectedIndex: SearchUtils.getPlaceholderIndexInLocalStorage(),
			placeholderSelectedValue: this.props.placeholder,
			placeholderList: [],
			showGenAiText: true,
		};
		const fetchSearchAutoCompleteItemsDebounceTime = 0.3 * 1000; // 300ms: seconds * milliseconds;

		this.fetchSearchAutoCompleteItems = debounce(
			this.fetchSearchAutoCompleteItems,
			fetchSearchAutoCompleteItemsDebounceTime,
			{
				leading: true,
			},
		);

		this.fetchFilesForAutocomplete = debounce(this.fetchFilesForAutocomplete, 500, {
			leading: false,
			trailing: true,
		});

		// debounce submitting search query by 500ms.
		const submitSearchQueryDebounceTime = 0.5 * 1000; // 500ms: seconds * milliseconds;

		this.submitSearchQuery = debounce(this.submitSearchQuery, submitSearchQueryDebounceTime, {
			leading: true,
		});
		globalThis.addEventListener('onSearchExit', this.onExit.bind(this));
		globalThis.addEventListener('onRestSearchAndExit', this.onRestSearchAndExit.bind(this));
		globalThis.addEventListener('onSearchSmallIconClick', this.onSearchIconClick.bind(this));
		this.onChange = this.onChange.bind(this);
		this.onBlur = this.onBlur.bind(this);
		this.onFocus = this.onFocus.bind(this);
		this.handleSearchKeydownEvent = this.handleSearchKeydownEvent.bind(this);
	}

	static getDerivedStateFromProps(nextProps, prevState) {
		return {
			...prevState,
			placeholderList: SearchBar.getSearchPlaceholdersFromConfig(nextProps),
		};
	}

	static getSearchPlaceholdersFromConfig(nextProps: SearchBarProps): string[] {
		let placeholderList = [nextProps.placeholder];
		const searchBarPlaceholders = nextProps.searchConfig?.ui?.searchBarPlaceholders;
		const searchBarPlaceholdersTranslated = FileTypeService.fileTypeDataObject?.translations?.searchBarPlaceholders;

		if (searchBarPlaceholders?.length > 0 && searchBarPlaceholdersTranslated) {
			placeholderList = searchBarPlaceholders.map((placeholder) => searchBarPlaceholdersTranslated[placeholder]);
		}
		return placeholderList;
	}

	/**
	 * to get the element div reference from searchBar Dropdown
	 * @param searchBarDropdownContainerRef
	 */
	getDivElementRefFromSearchDropdown = (searchBarDropdownContainerRef) => {
		this.searchBarDropdown = searchBarDropdownContainerRef;
	};
	/**
	 * Makes a network call to fetch Search Apps Metadata
	 */
	fetchSearchAppsMetadata = () => {
		this.props.fetchAppMetadataList(this.props.searchHostContext);
	};

	/**
	 * Makes a network call to fetch Pre Search Data
	 */
	fetchPreSearchData = () => {
		const context = this.props.searchHostContext;
		const locale = context.locale;
		const isCDNEnabledFromFloodgate = SearchUtils.isFloodgateFeatureInTest('ccs.cdn.recommendations', context);
		this.props.fetchPresearchData(context, this.props.logger, locale, isCDNEnabledFromFloodgate);
	};

	/**
	 * Makes a network call to fetch Search Auto-Complete Items
	 */
	fetchSearchAutoCompleteItems = (searchQuery) => {
		this.props.fetchSearchAutoCompleteItems(this.props.searchHostContext, searchQuery);
		this.props.fetchIntentShortcuts(this.props.searchHostContext, searchQuery);
	};

	/**
	 * Makes a network call to fetch files for Auto-Complete
	 */
	fetchFilesForAutocomplete = () => {
		this.props.fetchFilesForAutocomplete(false);
	};

	/**
	 * Makes a submit call for search query
	 */
	submitSearchQuery = (item: string) => {
		const trimmedSearchQuery = item.trim();
		SearchAnalytics.searchType = SearchAnalytics.currentSearch;
		const analytics = new SearchAnalytics(this.props.searchHostContext);
		analytics.initSearchAnalytics(trimmedSearchQuery);
		analytics.postSearchBarSubmit();
		this.setSearchHistoryWithNewSearchQuery(trimmedSearchQuery);
		this.props.logger.info('Search Bar', 'Logged Search Query', trimmedSearchQuery);
		this.props.onSubmit(trimmedSearchQuery);
		this.props.setExactTypedSearchQuery(trimmedSearchQuery);
		this.props.showSearchDropdownValue && this.props.showSearchDropdown(this.props.hostShowSearchDropdown, false);
		this.props.setSubmittedSearchQuery(true);
	};

	getPopularSearchTerms(): Record<string, any> {
		return this.props.presearchResultsPayload?.popularSearches?.searchResults || {};
	}

	fetchOnboardingSummaryItems = () => {
		const isOnboardingEnabledFromFloodgate = SearchUtils.isLeoFloodgateFeatureInTest(
			'ccs.ui.leo.onboarding',
			this.props.searchHostContext,
		);
		if (isOnboardingEnabledFromFloodgate && this.props.leoOnboardingSummaryItems.length === 0) {
			this.props.fetchOnboardingSummaryItems(this.props.searchHostContext, '');
		}
	};

	async componentDidMount() {
		this.props.theme && this.props.setTheme(this.props.theme);
		this.props.updateLeftnavStatus(this.props?.leftNavOn ?? false);

		this.props.fetchAppMetadataList(this.props.searchHostContext);
		this.fetchPreSearchData();
		globalThis.addEventListener('mousedown', this.handleSearchMouseClick);
		document.addEventListener('keydown', this.handleSearchKeydownEvent, true);
		document.addEventListener('searchBarMoveCursorToEnd', this.searchBarMoveCursorToEnd);
		globalThis.addEventListener('LocalDataRefreshASDE', SearchUtils.refreshLocalStorage);

		if (typeof this.props.initialValue === 'string') {
			this.props.setSearchQuery(this.props.initialValue);
			this.props.setSubmittedSearchQuery(true);
		}

		// Set a time out here. This is sent
		// when the component mounts, which means
		// it gets sent before we get the user's
		// account information from PIIP. This
		// informs us as to whether the user is
		// opting in/out for sending analytics.
		// We should have the results before 5 secs.
		if (this.props.searchHostContext.hostInfo.client === 'Helpx') {
			const analytics = new SearchAnalytics(this.props.searchHostContext);
			analytics.postSearchComponentLoad(this.props.leftNavOn || false);
		} else {
			setTimeout(() => {
				const analytics = new SearchAnalytics(this.props.searchHostContext);
				analytics.postSearchComponentLoad(this.props.leftNavOn || false);
			}, 5000);
		}
		if (this.props.leftNavOn) {
			this.searchBarContainerRef?.current?.classList.add('leftNavOn');
		}
		const isLeoEnabledFromFloodgate = SearchUtils.isFloodgateFeatureInTest(
			'ccs.ui.leo.experience',
			this.props.searchHostContext,
		);
		if (isLeoEnabledFromFloodgate) {
			await this.props.fetchUserPreferences();
			this.props.getLeoSession();
			this.fetchOnboardingSummaryItems();
		}
	}

	componentDidUpdate(prevProps: SearchBarProps) {
		const showSearchDropdownItems = SearchUtils.shouldShowSearchDropdownItems(
			this.props.searchQueryValue,
			this.props.filteredSearchDropdownItems,
		);

		if (prevProps.leftNavOn !== this.props.leftNavOn) {
			this.props.updateLeftnavStatus(this.props.leftNavOn);
			this.props.leftNavOn && this.searchBarContainerRef?.current?.classList.add('leftNavOn');
			!this.props.leftNavOn && this.searchBarContainerRef?.current?.classList.remove('leftNavOn');
		}

		if (
			(this.state.showSearchBarFocus || document?.activeElement?.id.includes('ccsearch-input')) &&
			this.props.showSearchDropdownValue &&
			showSearchDropdownItems
		) {
			// Show Search Dropdown if items to display and searchBar is in focus
			this.searchBarContainerRef?.current?.classList.add('dropdownOn');
		} else if (!this.props.showSearchDropdownValue || !showSearchDropdownItems) {
			this.searchBarContainerRef?.current?.classList.remove('dropdownOn');
		}

		const didSearchQueryChange = prevProps.searchQueryValue !== this.props.searchQueryValue;

		if (prevProps.theme !== this.props.theme) {
			this.props.setTheme(this.props.theme);
		}
		if (prevProps.initialValue !== this.props.initialValue) {
			this.props.setSearchQuery(this.props.initialValue);
		}

		if (didSearchQueryChange) {
			this.props.setFilteredSearchDropdownItems(
				'searchHistory',
				SearchUtils.getFilteredSearchHistoryItems(
					this.props.searchQueryValue,
					this.props.searchHistoryItemsValue,
				),
			);

			SearchAnalytics.dropDownData = { ...this.props.filteredSearchDropdownItems };
			if (prevProps.searchQueryValue.length === 0) {
				const analytics = new SearchAnalytics(this.props.searchHostContext);
				analytics.postSearchStartedTyping(this.props.searchQueryValue, this.props.leftNavOn || false);
			}
		}

		if (this.props.searchQueryValue) {
			if (!this.hasFetchedAppMetadataStatus) {
				this.props.fetchAppMetadataList(this.props.searchHostContext, true);
				this.hasFetchedAppMetadataStatus = true;
			}
			const showSearchDropdownItems = SearchUtils.shouldShowSearchDropdownItems(
				this.props.searchQueryValue,
				this.props.filteredSearchDropdownItems,
			);
			if (!this.props.submittedSearchQuery && this.state.showSearchBarFocus) {
				const dropDownStatusChanged = showSearchDropdownItems !== this.props.showSearchDropdownValue;
				dropDownStatusChanged &&
					this.props.showSearchDropdown(this.props.hostShowSearchDropdown, showSearchDropdownItems);
			}
			if (prevProps.hostShowSearchDropdown && !this.props.hostShowSearchDropdown) {
				this.props.showSearchDropdown(this.props.hostShowSearchDropdown, false);
			}
		}

		if (!this.props.searchQueryValue) {
			this.updateSearchDropdownCurrentSectionItem('');

			/**
			 * https://jira.corp.adobe.com/browse/CCS-3006
			 * If the host drop down value showing changes update here
			 *  ? Why the click event is being sent here
			 *  ! The event is being sent here with the understanding that when
			 *  ! showing the search icon the search bar was hidden so click will never be triggered
			 */
			if (!prevProps.hostShowSearchDropdown && this.props.hostShowSearchDropdown) {
				!this.props.showSearchDropdownValue &&
					this.props.showSearchDropdown(this.props.hostShowSearchDropdown, true);
				const analytics = new SearchAnalytics(this.props.searchHostContext);
				analytics.postSearchBarClick('input', this.props.leftNavOn || false);
			}

			if (this.state.showSearchBarFocus && !this.props.showSearchDropdownValue) {
				showSearchDropdownItems && this.props.showSearchDropdown(this.props.hostShowSearchDropdown, true);
			}

			if (prevProps.hostShowSearchDropdown && !this.props.hostShowSearchDropdown) {
				this.props.showSearchDropdown(this.props.hostShowSearchDropdown, false);
			}
		}

		const clearButton = this.searchBar.querySelector('.spectrum-ClearButton');

		if (
			clearButton &&
			(!this.searchBar.querySelector('.searchbar-clear-button') || prevProps.theme !== this.props.theme)
		) {
			const clearIconContainer = document.createElement('div');
			const clearIcon = <Close className='clearIcon' />;
			ReactDOM.render(clearIcon, clearIconContainer);

			clearButton.childNodes[0].remove();
			clearButton.appendChild(clearIconContainer);
		}
	}

	componentWillUnmount() {
		globalThis.removeEventListener('mousedown', this.handleSearchMouseClick);
		document.removeEventListener('keydown', this.handleSearchKeydownEvent, true);
		document.removeEventListener('searchBarMoveCursorToEnd', this.searchBarMoveCursorToEnd);
		globalThis.removeEventListener('LocalDataRefreshASDE', SearchUtils.refreshLocalStorage);
	}

	/**
	 * Determines whether or not we should show autocomplete based off of search configuration + host information.
	 * @returns {bool}
	 */
	shouldShowAutoComplete() {
		let shouldShowAutoComplete = true;

		// If the host is CCD
		if (
			this.props.searchHostContext.hostInfo.client === 'CCDesktop' &&
			this.props.searchHostContext.hostInfo.surface === 'CCDesktop'
		) {
			const hostUIState = this.props.searchHostContext.getHostUIState();
			const currentView = hostUIState.tab;

			if ((currentView as string) === 'yourWork') {
				shouldShowAutoComplete = false;
			}
		} else {
			// If host is anything but CCD
			shouldShowAutoComplete = this.props.searchConfig?.ui?.features?.autoComplete;
		}

		return shouldShowAutoComplete;
	}

	getSearchDropdownItemNumber(searchDropdownNodes, searchDropdownItem) {
		let searchDropdownItemNumber = 0;

		if (searchDropdownNodes) {
			for (let i = 0; i < searchDropdownNodes.length; i++) {
				if (searchDropdownNodes[i] === searchDropdownItem) {
					searchDropdownItemNumber = i;
				}
			}
		}

		return searchDropdownItemNumber;
	}

	/**
	 * Updates the id of the active search dropdown item.
	 * @param {string}
	 */
	updateSearchDropdownCurrentSectionItem(id) {
		let searchDropdownCurrentSectionItem = '';

		if (typeof id === 'string' && id.startsWith('search-dropdown') && id.split('search-dropdown-').length > 1) {
			searchDropdownCurrentSectionItem = id.split('search-dropdown-')[1];
		}

		this.props.setSearchDropdownCurrentSectionItem(searchDropdownCurrentSectionItem);
	}

	moveSearchDropdownFocusToFirstItem() {
		const searchInputDiv = this.searchBar.children;
		const searchDropdownListDom = document.querySelector('.search-dropdown-list');

		if (
			searchInputDiv &&
			searchInputDiv[0] instanceof HTMLInputElement &&
			searchInputDiv[0].id === 'ccsearch-input'
		) {
			searchInputDiv[0].blur();

			if (searchDropdownListDom) {
				const dropDownItems = searchDropdownListDom.querySelectorAll('[class^="search-dropdown"]');
				const list: any = dropDownItems
					? [...dropDownItems].filter((item) => item.id.startsWith('search-dropdown'))
					: [];
				this.updateSearchDropdownCurrentSectionItem(list[0].id);
				// @ts-ignore
				list[0].focus();

				if (this.isSearchQueryPreviewAllowed(list[0].id)) {
					this.props.setTempSearchQuery(list[0]?.dataset?.ccsQuery);
				}

				this.sendSearchKeyAnalytic(list[0]?.dataset?.ccsQuery, 'keydown');
			}
		}
	}

	/**
	 * Checks if the search query preview should be displayed for section item
	 * @param {string} id
	 * @returns {bool}
	 */
	isSearchQueryPreviewAllowed(id: string) {
		const searchQueryPreviewAllowedSection = ['search-dropdown-history', 'search-dropdown-auto-complete'];
		return searchQueryPreviewAllowedSection.some((section) => id.includes(section));
	}

	/**
	 * Set the temp query param for keyup and keydown
	 * @param {number} index
	 * @param {Element} elem
	 * @param {Array<string> } allSearchDropdownItems
	 */
	setTempQuery(elem: Element, index: number, allSearchDropdownItems: Array<string>) {
		const tempSearchQuery = allSearchDropdownItems[index];
		const el: HTMLElement = elem as HTMLElement;
		// We want to display search query preview for Non-Quick Links (History and Auto-Complete)
		if (this.isSearchQueryPreviewAllowed(elem.id)) {
			if (el?.dataset?.ccsQuery) {
				this.props.setTempSearchQuery(el.dataset.ccsQuery);
			} else if (typeof tempSearchQuery === 'string') {
				this.props.setTempSearchQuery(tempSearchQuery);
			}
		} else {
			// We don't want to display search query preview for Quick Links (Apps and Help)
			this.props.setTempSearchQuery('');
		}
	}

	/**
	 * Send the analytics for keyup and keydown
	 * @param {number} index
	 * @param {string} action
	 * @param {Array<string> } allSearchDropdownItems
	 */
	sendKeyUpDownAnalytics(index: number, action: string, allSearchDropdownItems: Array<string>, elem?: Element) {
		const el: HTMLElement = elem as HTMLElement;
		const tempSearchQuery = el?.dataset?.ccsQuery ?? allSearchDropdownItems[index];

		if (typeof tempSearchQuery === 'string') {
			this.setState({
				quickLinkSelected: false,
			});
			this.sendSearchKeyAnalytic(tempSearchQuery, action);
			// @ts-ignore
		} else if (tempSearchQuery?.label) {
			this.setState({
				quickLinkSelected: true,
			});
		}
	}

	isSearchBarSearchDropdown(e: Record<string, any>) {
		let isSearchBarSearchDropdown = false;

		if (e?.target) {
			const searchDropdownListDom = document.getElementsByClassName('search-dropdown-list');
			const isEventInsideSearchDropdownList = searchDropdownListDom?.[0]?.contains(e.target);
			const isEventInsideSearchBar = this.searchBar?.contains(e.target);
			const isEventInsideSearchBarDropdown = this.searchBarDropdown?.contains(e.target);

			if (isEventInsideSearchBar || isEventInsideSearchBarDropdown || isEventInsideSearchDropdownList) {
				isSearchBarSearchDropdown = true;
			}
		}

		return isSearchBarSearchDropdown;
	}

	isSearchBarInput(e: Record<string, any>) {
		const isSearchBarInput = false;

		if (e?.target) {
			const isEventInsideSearchBar = this.searchBar?.contains(e.target);
			const isClearButton = e.target.id === 'ccsearch-input-clear-button';
			return isEventInsideSearchBar && !isClearButton;
		}

		return isSearchBarInput;
	}

	// is clear Button is clicked
	isClearButtonSearchBarClicked(e: Record<string, any>) {
		const isClearButtonSearchBarClicked = false;

		if (e?.target) {
			const isEventInsideSearchBar = this.searchBar?.contains(e.target);
			const isClearButton = e.target.id === 'ccsearch-input-clear-button';
			return isEventInsideSearchBar && isClearButton;
		}

		return isClearButtonSearchBarClicked;
	}

	handleSearchMouseClick = (e) => {
		if (e.target.className === 'searchbar-clear-button') {
			this.updateSearchDropdownCurrentSectionItem('');
			const analytics = new SearchAnalytics(this.props.searchHostContext);
			analytics.postSearchBarClick('clear', this.props.leftNavOn || false);
		}

		const isSearchBar = e?.target && this.searchBar?.contains(e.target);
		const isSearchBarSearchDropdown = this.isSearchBarSearchDropdown(e);

		if (isSearchBar) {
			if (this.props.searchHistoryItemsValue) {
				SearchAnalytics.searchHistoryLen = this.props.searchHistoryItemsValue.length;
			}

			SearchUtils.didResetSearchBar = false;

			if (!this.props.value || this.props.value.length === 0) {
				SearchUtils.didResetSearchBar = true;
			}
		}

		if (!isSearchBarSearchDropdown) {
			// Remove search bar focus ring
			this.setState({
				showSearchBarFocus: false,
			});

			if (this.props.showSearchDropdownValue) {
				this.props.showSearchDropdown(this.props.hostShowSearchDropdown, false);
			}
		}

		// When the user clicks anywhere inside the Dropdown, close it
		if (isSearchBarSearchDropdown && !isSearchBar) {
			this.setState({
				showSearchBarFocus: false,
			});
		}
	};
	handleSearchKeydownEvent = (e: Record<string, any>) => {
		const keyCode = e.keyCode;
		const keyValue = e.key;
		const searchDropdownListDom = document.querySelector('.search-dropdown-list');
		const exclusions = [
			'search-dropdown-quick-links-link-item-',
			'search-dropdown-gen-ai-summary-item',
			'search-dropdown-quick-action-item-',
			'search-dropdown-history-item-',
		];
		const includeInNavigation = (id) => exclusions.every((ex) => !id.startsWith(ex));
		// Up Arrow Key
		if (keyCode === 38 || keyValue === 'ArrowUp') {
			if (
				searchDropdownListDom &&
				document.activeElement?.id &&
				document.activeElement.id.includes('search-dropdown') &&
				exclusions.every((exclusion) => !document.activeElement.id.includes(exclusion))
			) {
				// Show search bar focus ring
				this.setState({
					showSearchBarFocus: true,
				});
				const dropDownItems = searchDropdownListDom.querySelectorAll('[class^="search-dropdown"]');
				const list = dropDownItems
					? [...dropDownItems].filter(
							(item) => item.id.startsWith('search-dropdown') && includeInNavigation(item.id),
						)
					: [];
				const itemNumber = this.getSearchDropdownItemNumber(list, document.activeElement);

				if (list && typeof itemNumber === 'number') {
					// Preventing Browser from scrolling
					e.preventDefault();

					if (itemNumber === 0) {
						if (list[0] instanceof HTMLElement) {
							list[0].blur();
							const searchInputDiv = document.getElementById('ccsearch-input');

							if (searchInputDiv && searchInputDiv !== null) {
								this.updateSearchDropdownCurrentSectionItem(searchInputDiv.id);
								searchInputDiv.focus();
							}

							this.props.setTempSearchQuery('');
						}
					} else {
						this.updateSearchDropdownCurrentSectionItem(list[itemNumber - 1].id);
						// @ts-ignore
						list[itemNumber].blur();
						// @ts-ignore
						list[itemNumber - 1].focus();

						if (this.props.filteredSearchDropdownItems) {
							this.setTempQuery(
								list[itemNumber - 1],
								itemNumber - 1,
								SearchUtils.listAllSearchDropdownItems(this.props.filteredSearchDropdownItems),
							);
							this.sendKeyUpDownAnalytics(
								itemNumber - 1,
								'keyup',
								SearchUtils.listAllSearchDropdownItems(this.props.filteredSearchDropdownItems),
								list[itemNumber - 1],
							);
						}
					}
				}
			}
		} else if (keyCode === 40 || keyValue === 'ArrowDown') {
			if (
				document.activeElement?.id &&
				exclusions.every((exclusion) => !document.activeElement.id.includes(exclusion))
			) {
				if (document.activeElement?.id === 'ccsearch-input') {
					this.fetchSearchHistory();
					// Show search bar focus ring
					this.setState({
						showSearchBarFocus: true,
					});
					// Preventing Browser from scrolling
					e.preventDefault();
					const searchInputDiv = document.getElementById('ccsearch-input');

					if (searchInputDiv && searchInputDiv !== null) {
						this.props.fetchPresearchRecentFiles();
						this.props.showSearchDropdown(this.props.hostShowSearchDropdown, true);

						// Check if IME is available and composing
						if (!(e.isComposing !== undefined && e.isComposing === true)) {
							setTimeout(() => {
								this.moveSearchDropdownFocusToFirstItem();
							}, 0);
						}
					}
				} else if (document.activeElement?.id?.includes('search-dropdown')) {
					// Show search bar focus ring
					this.setState({
						showSearchBarFocus: true,
					});
					// Preventing Browser from scrolling
					e.preventDefault();
					const dropDownItems = searchDropdownListDom.querySelectorAll('[class^="search-dropdown"]');
					const list = dropDownItems
						? [...dropDownItems].filter(
								(item) => item.id.startsWith('search-dropdown') && includeInNavigation(item.id),
							)
						: [];
					const itemNumber = this.getSearchDropdownItemNumber(list, document.activeElement);

					if (list && typeof itemNumber === 'number' && itemNumber !== list.length - 1) {
						this.updateSearchDropdownCurrentSectionItem(list[itemNumber + 1].id);
						// @ts-ignore
						list[itemNumber].blur();
						// @ts-ignore
						list[itemNumber + 1].focus();
						if (this.props.filteredSearchDropdownItems) {
							this.setTempQuery(
								list[itemNumber + 1],
								itemNumber + 1,
								SearchUtils.listAllSearchDropdownItems(this.props.filteredSearchDropdownItems),
							);
							this.sendKeyUpDownAnalytics(
								itemNumber + 1,
								'keydown',
								SearchUtils.listAllSearchDropdownItems(this.props.filteredSearchDropdownItems),
								list[itemNumber + 1],
							);
						}
					}
				}
			}
		} else if (keyCode === 27 || keyValue === 'Escape') {
			if (
				document.getElementById('search-bar-container')?.contains(e.target as HTMLElement) &&
				exclusions.every((exclusion) => !document.activeElement.id.includes(exclusion))
			) {
				// Preventing Escape from propagating
				this.props.setTempSearchQuery('');
				this.setState({
					showSearchBarFocus: false,
				});
				if (this.props.showSearchDropdownValue) {
					this.props.showSearchDropdown(this.props.hostShowSearchDropdown, false);
				}
				// shift the focus to input element
				const inputElements = this.searchBar.getElementsByTagName('input');
				inputElements.length && inputElements[0].focus();
			}
		} else if (keyCode === 13 || keyValue === 'Enter') {
			this.sendSearchKeyAnalytic(SearchAnalytics.keyword, 'select');

			if (document.activeElement?.id?.includes('search-dropdown')) {
				// Added below if statement for flow
				if (document.activeElement) {
					// @ts-ignore
					document.activeElement.click();
				}

				this.props.setTempSearchQuery('');

				if (this.state.quickLinkSelected) {
					this.searchBarMoveCursorToEnd();
					this.setState({
						quickLinkSelected: false,
					});
				}
			}

			if (document?.activeElement?.id?.includes('ccsearch-input')) {
				this.fetchSearchHistory();
				this.setState({
					showSearchBarFocus: true,
				});
			}
		} else if ((keyCode === 9 || keyValue === 'Tab') && e.shiftKey) {
			if (searchDropdownListDom) {
				this.setState({
					showSearchBarFocus: false,
				});
				if (this.props.showSearchDropdownValue) {
					this.props.showSearchDropdown(this.props.hostShowSearchDropdown, false);
				}
			}
		} else if ((keyCode === 9 || keyValue === 'Tab') && !e.shiftKey) {
			const clearInputButton = document.getElementById('ccsearch-input-clear-button');
			if (document.getElementById('search-bar-container')?.contains(e.target as HTMLElement)) {
				if (clearInputButton && !document.activeElement.id.includes('ccsearch-input-clear-button')) {
					e.stopPropagation();
					e.preventDefault();
					clearInputButton.focus();
				}
				if (searchDropdownListDom) {
					this.setState({
						showSearchBarFocus: false,
					});
					if (this.props.showSearchDropdownValue) {
						this.props.showSearchDropdown(this.props.hostShowSearchDropdown, false);
					}
				}
			}
		}
	};

	/*
	 * Moves the cursor in the Search Bar to the end of the Search Query
	 */
	searchBarMoveCursorToEnd = () => {
		const searchInput = document.getElementById('ccsearch-input');

		if (searchInput instanceof HTMLInputElement) {
			this.updateSearchDropdownCurrentSectionItem('');
			const len = searchInput.value.length;
			// Adding a 50ms delay to move cursor to the end after the User clicks
			setTimeout(() => searchInput.setSelectionRange(len, len), 50);
		}
	};

	/*
	 * Adds the Search History Item to local storage
	 */
	async setSearchHistoryWithNewSearchQuery(item: string) {
		if (
			typeof item === 'string' &&
			item !== '' &&
			this.props.searchHistoryItemsValue &&
			Array.isArray(this.props.searchHistoryItemsValue)
		) {
			const processedSearchHistoryItems = SearchUtils.setSearchHistoryWithSearchQuery(
				this.props.searchHistoryItemsValue,
				item,
			);
			// Updates the store with the latest history array
			this.props.setSearchHistoryItems(processedSearchHistoryItems);
		} else {
			const processedSearchHistoryItems = SearchUtils.setSearchHistoryWithSearchQuery([], item);
			this.props.setSearchHistoryItems(processedSearchHistoryItems);
		}

		try {
			const authenticated = SearchHostContext.isAuthenticated();
			if (
				authenticated &&
				this.props.searchHostContext.featureFlags.includes('temp_enable_search_history_server')
			) {
				if (item !== '') {
					const searchService = new SearchService(this.props.searchHostContext);
					await searchService.pushSearchHistory(item);
				}
			}
		} catch (e) {
			this.props.logger.error(
				'Search Bar',
				'Problem creating/updating search history to search server on search bar submit',
				e,
			);
		}
	}

	onFocus() {
		if (this.props.searchConfig?.ui?.searchBarPlaceholders) {
			const placeholderSelectedIndex =
				(this.state.placeholderSelectedIndex + 1) % this.state.placeholderList.length;
			const placeholderSelectedId = this.props.searchConfig.ui.searchBarPlaceholders[placeholderSelectedIndex];
			const placeholderSelectedValue = this.state.placeholderList[placeholderSelectedIndex];
			this.setState((previousState) => ({
				...previousState,
				placeholderSelectedIndex,
				placeholderSelectedValue,
			}));
			SearchUtils.setPlaceHolderIndexInLocalStorage(placeholderSelectedIndex);
			const analytics = new SearchAnalytics(this.props.searchHostContext);
			analytics.postSearchBarPlaceholderRender(placeholderSelectedId);
		}
	}

	onBlur() {
		this.setState({ placeholderSelectedValue: this.props.placeholder });
	}

	onChange(item: string, e: any, trigger: { from: string }) {
		// Empty searchAutoCompleteData data because getAppsForQuickLinks(in searchutils) is
		// been called before even we fetch the new autosuggestion data.
		// Fix for Bug CCS-1339 Quick links are appearing for instance for wrong search when search start with P, X, L... etc
		this.props.setSubmittedSearchQuery(false);
		this.props.setTempSearchQuery('');
		this.props.setSearchQuery(item);

		if (this.props.onChange) {
			this.props.onChange(item);
		}

		if (trigger.from === 'input') {
			if (item?.length > 0) {
				this.props.showSearchDropdown(this.props.hostShowSearchDropdown, true);
				this.fetchSearchAutoCompleteItems(item);
				this.fetchFilesForAutocomplete();
			} else {
				// If search query is empty string, we need to reset searchAutoCompleteItems
				this.props.fetchSearchAutoCompleteItems(this.props.searchHostContext, '');
				this.props.fetchIntentShortcuts(this.props.searchHostContext, '');
				this.props.fetchFilesForAutocomplete();
				this.props.fetchPresearchRecentFiles();
			}
		}

		if (trigger.from === 'clearButton') {
			this.props.showSearchDropdown(this.props.hostShowSearchDropdown, false);
			this.props.fetchSearchAutoCompleteItems(this.props.searchHostContext, '');
			this.props.fetchIntentShortcuts(this.props.searchHostContext, '');
			this.props.fetchPresearchRecentFiles();
			this.props.fetchFilesForAutocomplete();
		}
	}

	onExit() {
		const searchInput = document.getElementById('ccsearch-input');

		if (searchInput instanceof HTMLInputElement) {
			if (this.searchBar) {
				const clearButton = this.searchBar.querySelector('.spectrum-ClearButton');
				clearButton?.click();
			}
			searchInput.blur();
			// Hide search bar focus ring
			this.setState({
				showSearchBarFocus: false,
			});
			this.props.resetSearch(this.props.searchHostContext, this.props.logger);
		}

		const analytics = new SearchAnalytics(this.props.searchHostContext);
		analytics.postSearchBarClick('exit', this.props.leftNavOn || false);
	}

	onRestSearchAndExit() {
		const searchInput = document.getElementById('ccsearch-input');
		searchInput.blur();
		this.setState(
			{
				showSearchBarFocus: false,
			},
			() => {
				if (this.props.showSearchDropdownValue) {
					this.props.showSearchDropdown(this.props.hostShowSearchDropdown, false);
				}
				this.props.resetSearch(this.props.searchHostContext, this.props.logger);
				const analytics = new SearchAnalytics(this.props.searchHostContext);
				analytics.postSearchBarClick('exit', this.props.leftNavOn || false);
			},
		);
	}

	onSearchIconClick() {
		const searchInput = this.searchBar.children;
		searchInput[0] instanceof HTMLInputElement && searchInput[0].id === 'ccsearch-input' && searchInput[0].click();
	}

	sendSearchKeyAnalytic(searchQuery: string, direction: string) {
		SearchAnalytics.keyword = searchQuery;
		const analytics = new SearchAnalytics(this.props.searchHostContext);
		analytics.postSuggestionAction(direction, 'suggestion');
	}

	onSearchBarWrapperClick(e: Record<string, any>) {
		const searchInput = this.searchBar.children;
		const isSearchInputTarget = this.isSearchBarInput(e);
		const leoSearchbarOnboarding = SearchUtils.isLeoFloodgateFeatureInTest(
			'ccs.ui.leo.searchBarOnboarding',
			this.props.searchHostContext,
		);
		leoSearchbarOnboarding && removeOnboardingSection();
		const isClearButtonSearchBarClicked = this.isClearButtonSearchBarClicked(e);
		if (
			(isSearchInputTarget &&
				searchInput &&
				searchInput[0] instanceof HTMLInputElement &&
				searchInput[0].id === 'ccsearch-input') ||
			(isClearButtonSearchBarClicked &&
				searchInput &&
				searchInput[2] instanceof HTMLButtonElement &&
				searchInput[2].id === 'ccsearch-input-clear-button')
		) {
			searchInput[0].focus();
			// Show search bar focus ring
			this.setState({
				showSearchBarFocus: true,
			});
			const showSearchDropdownItems = SearchUtils.shouldShowSearchDropdownItems(
				this.props.searchQueryValue,
				this.props.filteredSearchDropdownItems,
			);

			// Show Search Dropdown if items to display
			if (showSearchDropdownItems && !this.props.showSearchDropdownValue) {
				this.props.showSearchDropdown(this.props.hostShowSearchDropdown, true);
			}

			if (isSearchInputTarget) {
				const analytics = new SearchAnalytics(this.props.searchHostContext);
				analytics.postSearchBarClick('input', this.props.leftNavOn || false);
				this.props.fetchPresearchRecentFiles();
				this.fetchSearchHistory();
				this.initializeSearchRecommendations();
			}
		}
	}

	fetchSearchHistory() {
		this.props.searchQueryValue === '' && this.props.fetchSearchHistoryItems(this.props.searchHostContext);
	}

	initializeSearchRecommendations() {
		const recommendationsFromConfig = this.props.searchConfig?.ui?.preSearchRecommendations;

		if (
			recommendationsFromConfig &&
			this.props.searchAppMetadataList &&
			!(this.props.recommendationsGroups && Object.keys(this.props.recommendationsGroups).length)
		) {
			this.props.updatePreSearchRecommendations(recommendationsFromConfig);
		}
	}

	getSearchQuery() {
		let searchQuery = '';

		if (this.props.tempSearchQueryValue === '' && this.props.searchQueryValue === '') {
			searchQuery = this.props.value;
			searchQuery = searchQuery ?? '';
			this.props.setTempSearchQuery('');
			this.props.setSearchQuery(searchQuery);
		} else if (this.props.tempSearchQueryValue === '') {
			searchQuery = this.props.searchQueryValue;
		} else {
			searchQuery = this.props.tempSearchQueryValue;
		}

		return searchQuery;
	}

	getSearchBar() {
		const searchBar = (
			<Search
				id='ccsearch-input'
				className={this.state.showSearchBarFocus ? 'search-searchbar' : ''}
				// eslint-disable-next-line react/no-find-dom-node
				ref={(component) => {
					this.searchBar = ReactDOM.findDOMNode(component);
				}}
				placeholder={this.state.placeholderSelectedValue}
				maxLength={this.props.maxLength}
				role='combobox'
				aria-controls='search-suggestions-list'
				aria-haspopup='listbox'
				aria-expanded={this.props.showSearchDropdownValue}
				aria-activedescendant={
					this.props.searchDropdownCurrentSectionItem === ''
						? undefined
						: this.props.searchDropdownCurrentSectionItem
				}
				tabIndex={0}
				autoComplete='off'
				onChange={this.onChange}
				onSubmit={(e) => this.submitSearchQuery(e)}
				onBlur={() => this.onBlur()}
				onFocus={() => this.onFocus()}
				value={this.getSearchQuery()}
				searchHostContext={this.props.searchHostContext}
				logger={this.props.logger}
			/>
		);

		return searchBar;
	}

	getSearchDropdown() {
		const searchDropdown = (
			<SearchDropdown
				searchHostContext={this.props.searchHostContext}
				// @ts-ignore
				id='search-input-dropdown'
				searchQuery={this.props.searchQueryValue}
				hostShowSearchDropdown={this.props.hostShowSearchDropdown}
				onSubmit={this.props.onSubmit}
				showAutoComplete={this.shouldShowAutoComplete()}
				logger={this.props.logger}
				searchConfig={this.props.searchConfig}
				getSearchBarContainerRef={this.getDivElementRefFromSearchDropdown}
			/>
		);
		return searchDropdown;
	}

	render() {
		const colorTheme = this.props.theme ? `${this.props.theme}-mode` : 'light-mode';
		const { intl } = this.props;
		const leoSearchbarOnboarding = SearchUtils.isLeoFloodgateFeatureInTest(
			'ccs.ui.leo.searchBarOnboarding',
			this.props.searchHostContext,
		);

		const setContainerRef = (el: HTMLDivElement | null) => {
			(this.searchBarContainerRef as React.MutableRefObject<HTMLDivElement | null>).current = el;
			if (this.props.searchBarRootRef) {
				this.props.searchBarRootRef.current = el;
			}
		};

		return (
			<>
				<div
					ref={setContainerRef}
					id='search-bar-container'
					className={`search-bar-container ${this.props.searchHostContext.uiStyling?.styleSpecifier ?? ''}`}
					onClick={this.onSearchBarWrapperClick.bind(this)}
				>
					{this.getSearchBar()}
					{leoSearchbarOnboarding &&
						!this.props.showSearchDropdownValue &&
						!this.state.showSearchBarFocus &&
						this.props.searchQueryValue === '' &&
						appendOnboardingSection(this.searchBar, intl, colorTheme)}
					{this.props.showSearchDropdownValue ? (
						<div className={colorTheme}>{this.getSearchDropdown()}</div>
					) : null}
				</div>
				<div id='ccs-searchbar-hidden-trigger' />
			</>
		);
	}
}

const mapStateToProps = ({ search }: RootState) => ({
	searchHistoryItemsValue: search.searchHistoryItems,
	presearchResultsPayload: search.presearchResultsPayload,
	filteredSearchDropdownItems: search.filteredSearchDropdownItems,
	searchAutoCompleteItemsValue: search.searchAutoCompleteItems,
	leoOnboardingSummaryItems: search.leoOnboardingSummaryItems,
	searchQueryValue: search.searchQuery,
	tempSearchQueryValue: search.tempSearchQuery,
	exactTypedSearchQuery: search.exactTypedSearchQuery,
	showSearchDropdownValue: search.showSearchDropdown,
	submittedSearchQuery: search.submittedSearchQuery,
	searchConfig: search.searchConfig,
	srpCurrentTab: search.srpCurrentTab,
	currentSearchView: search.currentSearchView,
	searchAppMetadataList: search.searchAppMetadataList,
	searchDropdownCurrentSectionItem: search.searchDropdownCurrentSectionItem,
	recommendationsGroups: search.preSearchRecommendations,
});

const mapDispatchToProps = {
	updateLeftnavStatus,
	fetchSearchAutoCompleteItems,
	fetchOnboardingSummaryItems,
	fetchIntentShortcuts,
	fetchPresearchRecentFiles,
	fetchFilesForAutocomplete,
	fetchSearchAssetsResults,
	setSubmittedSearchQuery,
	fetchSearchHistoryItems,
	setSearchHistoryItems,
	setFilteredSearchDropdownItems,
	setSearchQuery,
	setExactTypedSearchQuery,
	setTempSearchQuery,
	showSearchDropdown,
	fetchAppMetadataList,
	setSearchDropdownCurrentSectionItem,
	resetSearch,
	fetchPresearchData,
	setTheme,
	getLeoSession,
	fetchUserPreferences,
	updatePreSearchRecommendations,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(injectIntl(SearchBar));
